import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../Router/all_routes";
import {
  PlusCircle,
  Edit,
  Trash2,
  Image as ImageIcon,
  Search,
  Loader2,
  FileIcon,
  Video,
  Download,
  X,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL } from "../../api/header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Modal from "@/components/ui/modal";
import { ScrollArea } from "@/components/ui/scroll-area";

const Media = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    file: null,
    name: "",
    alt_text: "",
    caption: "",
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  useEffect(() => {
    if (!hasPermission("media read") && !hasPermission("media library view")) {
      navigate(all_routes.dashboard);
    } else {
      getMediaItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMediaItems = async () => {
    if (!hasPermission("media read") && !hasPermission("media library view"))
      return;
    try {
      const res = await apiClient.get(`${API_BASE_URL}/media`);
      if (res.status === 200 || res.status === 201) {
        const sortedMedia = res.data.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        setMediaItems(sortedMedia);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch media");
    }
  };

  const filteredData = mediaItems.filter(
    (entry) =>
      (entry.name &&
        entry.name.toLowerCase().includes(searchText.toLowerCase())) ||
      (entry.file_name &&
        entry.file_name.toLowerCase().includes(searchText.toLowerCase())) ||
      (entry.alt_text &&
        entry.alt_text.toLowerCase().includes(searchText.toLowerCase())) ||
      (entry.caption &&
        entry.caption.toLowerCase().includes(searchText.toLowerCase())),
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setFormData({ ...formData, file: file });
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      file: null,
      name: "",
      alt_text: "",
      caption: "",
    });
  };

  const handleSave = async () => {
    if (formData.id && !hasPermission("media update")) {
      toast.error("You do not have permission to update media");
      return;
    }
    if (!formData.id && !hasPermission("media create")) {
      toast.error("You do not have permission to upload media");
      return;
    }
    setIsLoading(true);
    try {
      if (!formData.id && !formData.file) {
        toast.error("Please select a file to upload");
        setIsLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      if (formData.file) formDataToSend.append("file", formData.file);
      if (formData.name) formDataToSend.append("name", formData.name);
      if (formData.alt_text)
        formDataToSend.append("alt_text", formData.alt_text);
      if (formData.caption) formDataToSend.append("caption", formData.caption);

      const url = formData.id ? `/media/${formData.id}` : `/media`;
      const method = formData.id ? "put" : "post";

      const res = await apiClient({
        method,
        url,
        data: formDataToSend,
      });

      if (res.status === 200 || res.status === 201) {
        setIsLoading(false);
        toast.success("Media saved successfully");
        setModalOpen(false);
        getMediaItems();
        resetForm();
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(error.response?.data?.message || "Failed to save media");
    }
  };

  const handleEdit = async (media) => {
    setFormData({
      id: media.id,
      file: null,
      name: media.name || "",
      alt_text: media.alt_text || "",
      caption: media.caption || "",
    });
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!hasPermission("media delete")) {
      toast.error("You do not have permission to delete media");
      return;
    }
    try {
      const res = await apiClient.delete(`${API_BASE_URL}/media/${deleteId}`);
      if (res.status === 204) {
        toast.success("Media deleted successfully");
        getMediaItems();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete media");
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  const isImageFile = (mimeType) => mimeType?.startsWith("image/");
  const isVideoFile = (mimeType) => mimeType?.startsWith("video/");

  return (
    <div className="page-wrapper">
      <div className="content p-6 max-w-[100vw] overflow-x-hidden">
        <PageHeader>
          <PageHeaderHeading
            title="Media Library"
            subtitle="Manage your media files and assets"
          />
          <PageHeaderActions>
            {hasPermission("media create") && (
              <Button onClick={() => setModalOpen(true)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Upload Media
              </Button>
            )}
          </PageHeaderActions>
        </PageHeader>

        <Card className="mb-6">
          <CardContent className="p-4 flex flex-col md:flex-row items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search media files..."
                className="pl-9 h-10 w-full md:max-w-sm"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <Badge variant="secondary" className="px-3 py-1">
              {filteredData.length} file{filteredData.length !== 1 ? "s" : ""}
            </Badge>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredData.length === 0 ? (
            <div className="col-span-full flex flex-col items-center justify-center py-20 bg-muted/20 rounded-xl border-2 border-dashed">
              <ImageIcon
                size={48}
                className="text-muted-foreground mb-4 opacity-50"
              />
              <h3 className="text-lg font-semibold text-muted-foreground">
                No media files found
              </h3>
              <p className="text-sm text-muted-foreground">
                {searchText
                  ? "Try adjusting your search terms"
                  : "Upload your first media file to get started"}
              </p>
            </div>
          ) : (
            filteredData.map((media) => (
              <Card
                key={media.id}
                className="group relative overflow-hidden border-none shadow-sm hover:shadow-md transition-shadow"
              >
                <div
                  className="aspect-square bg-muted/30 relative cursor-pointer"
                  onClick={() => {
                    setSelectedMedia(media);
                    setLightboxOpen(true);
                  }}
                >
                  {isImageFile(media.mime_type) ? (
                    <img
                      src={media.url}
                      alt={media.alt_text || media.name}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {isVideoFile(media.mime_type) ? (
                        <Video size={40} className="text-primary opacity-50" />
                      ) : (
                        <FileIcon
                          size={40}
                          className="text-primary opacity-50"
                        />
                      )}
                    </div>
                  )}

                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {hasPermission("media update") && (
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(media);
                        }}
                      >
                        <Edit size={14} />
                      </Button>
                    )}
                    {hasPermission("media delete") && (
                      <Button
                        size="icon"
                        variant="destructive"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(media.id);
                        }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="p-2 border-t">
                  <p
                    className="text-xs font-semibold truncate"
                    title={media.name || media.file_name}
                  >
                    {media.name || media.file_name}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {media.size
                      ? `${(media.size / 1024).toFixed(1)} KB`
                      : "0 KB"}
                  </p>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Upload/Edit Modal */}
      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={formData.id ? "Edit Media" : "Upload New Media"}
      >
        <div className="space-y-4 px-1 py-2">
          {!formData.id && (
            <div className="space-y-2">
              <Label>
                Select File <span className="text-destructive">*</span>
              </Label>
              <Input
                type="file"
                onChange={handleFileChange}
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                className="h-auto py-2"
              />
              <p className="text-[10px] text-muted-foreground">
                Max size: 10MB. Supports Images, Videos, PDFs, Documents
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              placeholder="Enter name (optional)"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              maxLength={255}
            />
            <div className="flex justify-end">
              <span className="text-[10px] text-muted-foreground">
                {formData.name.length}/255
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Alt Text</Label>
            <Input
              placeholder="Alternative text for accessibility"
              value={formData.alt_text}
              onChange={(e) =>
                setFormData({ ...formData, alt_text: e.target.value })
              }
              maxLength={255}
            />
            <p className="text-[10px] text-muted-foreground">
              Describes the media for screen readers.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Caption</Label>
            <Textarea
              placeholder="Enter a caption or description"
              value={formData.caption}
              onChange={(e) =>
                setFormData({ ...formData, caption: e.target.value })
              }
              maxLength={1000}
              className="resize-none"
            />
            <div className="flex justify-end">
              <span className="text-[10px] text-muted-foreground">
                {formData.caption.length}/1000
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {formData.id ? "Update Media" : "Upload Media"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onOpenChange={(open) => {
          setLightboxOpen(open);
          if (!open) setSelectedMedia(null);
        }}
        title={selectedMedia?.name || selectedMedia?.file_name || "Preview"}
        maxWidth="xl"
      >
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          {selectedMedia && (
            <>
              <ScrollArea className="w-full max-h-[70vh]">
                <div className="flex flex-col items-center p-4">
                  {isImageFile(selectedMedia.mime_type) ? (
                    <img
                      src={selectedMedia.url}
                      alt={selectedMedia.alt_text || selectedMedia.name}
                      className="max-w-full h-auto rounded-lg shadow-lg"
                    />
                  ) : isVideoFile(selectedMedia.mime_type) ? (
                    <video
                      controls
                      className="max-w-full h-auto rounded-lg shadow-lg"
                    >
                      <source
                        src={selectedMedia.url}
                        type={selectedMedia.mime_type}
                      />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="flex flex-col items-center justify-center p-20 bg-muted/20 rounded-xl border-2 border-dashed w-full">
                      <FileIcon
                        size={80}
                        className="text-primary opacity-30 mb-4"
                      />
                      <h4 className="text-lg font-bold">
                        {selectedMedia.file_name}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-6">
                        This file type cannot be previewed
                      </p>
                      <Button asChild>
                        <a
                          href={selectedMedia.url}
                          download={selectedMedia.file_name}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download File
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {selectedMedia.caption && (
                <div className="mt-6 p-4 bg-muted/30 rounded-lg w-full">
                  <p className="text-sm text-center italic text-muted-foreground">
                    {selectedMedia.caption}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              media file and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Media;
